import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/react-query.config';

type StartLike = Date | string | number | null | undefined;

function toStartMs(value: StartLike): number {
  if (value == null) return NaN;
  if (typeof value === 'number') {
    // Treat small numbers as unix seconds (backend start_utc_timestamp).
    return value < 1e12 ? value * 1000 : value;
  }
  return new Date(value).getTime();
}

/**
 * When the next contest start time is reached, bump a tick (so callers can
 * re-derive OPEN → live from wall clock) and invalidate pickem queries.
 * Critical in mobile webviews where users cannot manually refresh.
 */
export function usePickemKickoffRefresh(startDates: StartLike[]): number {
  const queryClient = useQueryClient();
  const [tick, setTick] = useState(0);

  // Stabilize array identity so effect/memo don't re-run every render.
  const startKey = startDates
    .map(value => {
      const ms = toStartMs(value);
      return Number.isFinite(ms) ? String(ms) : '';
    })
    .filter(Boolean)
    .sort()
    .join('|');

  const nextStartMs = useMemo(() => {
    const now = Date.now();
    const upcoming = startKey
      .split('|')
      .filter(Boolean)
      .map(Number)
      .filter(ms => ms > now);

    return upcoming.length > 0 ? Math.min(...upcoming) : null;
  }, [startKey]);

  useEffect(() => {
    if (nextStartMs == null) return;

    const delay = Math.max(0, nextStartMs - Date.now()) + 250;
    const timeoutId = window.setTimeout(() => {
      setTick(current => current + 1);
      void queryClient.invalidateQueries({ queryKey: queryKeys.pickem.all });
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [nextStartMs, queryClient]);

  return tick;
}
